import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Check } from 'lucide-react'
import {
  type ScopeTierInForceFilter,
  type ScopeTierBatchSearchFilters,
  type ScopeTierStatus,
  type ScopeTierStatusFilter,
  useScopeTierBatchDocumentMatches,
  useScopeTierBatchSearch,
  useScopeTierTags,
  useSetScopeTierStatusesBulk,
} from '@/shared/hooks/api/radarScopeTier'
import type { LegislationTypeCode } from '@artem-lex/common-server/radar/legislationTypes'
import { getApiErrorMessage } from '@/shared/utils/getApiErrorMessage'
import { getBatchSearchResultsState } from './batchSearchResultsState'
import LegislationTypeFilter from './LegislationTypeFilter'

export const MIN_BATCH_QUERY_LENGTH = 4
type BatchActionValue = ScopeTierStatus | 'add_tags' | 'remove_tags'

type Props = {
  defaultStatus: ScopeTierStatusFilter
  defaultTiers: Array<1 | 2 | 3>
  defaultTypes: LegislationTypeCode[]
  defaultIncludeUnassigned: boolean
  defaultInForceFilter: ScopeTierInForceFilter
  defaultIncludeTrashed: boolean
  onDismiss: () => void
}

export default function BatchEditPanel({
  defaultStatus,
  defaultTiers,
  defaultTypes,
  defaultIncludeUnassigned,
  defaultInForceFilter,
  defaultIncludeTrashed,
  onDismiss,
}: Props) {
  const [query, setQuery] = useState('')
  const [sectionQuery, setSectionQuery] = useState('')
  const [status, setStatus] = useState<ScopeTierStatusFilter>(defaultStatus)
  const [includeTrashed, setIncludeTrashed] = useState(defaultIncludeTrashed)
  const [includeUnassigned, setIncludeUnassigned] = useState(defaultIncludeUnassigned)
  const [inForceFilter, setInForceFilter] =
    useState<ScopeTierInForceFilter>(defaultInForceFilter)
  const [selectedTiers, setSelectedTiers] = useState<Array<1 | 2 | 3>>(defaultTiers)
  const [selectedTypes, setSelectedTypes] = useState<LegislationTypeCode[]>(defaultTypes)
  const [excludedDocumentIds, setExcludedDocumentIds] = useState<string[]>([])
  const [searchVersion, setSearchVersion] = useState(0)
  const [selectionById, setSelectionById] = useState<Record<string, boolean>>({})
  const [expandedDocumentId, setExpandedDocumentId] = useState<string | null>(null)
  const [action, setAction] = useState<BatchActionValue>('flagged')
  const [isTagMenuOpen, setIsTagMenuOpen] = useState(false)
  const [batchTagDraft, setBatchTagDraft] = useState('')
  const [batchActionTags, setBatchActionTags] = useState<string[]>([])
  const [resultMessage, setResultMessage] = useState<string | null>(null)
  const tagMenuInputRef = useRef<HTMLInputElement | null>(null)

  const draftFilters = useMemo(
    () => ({
      q: query.trim(),
      sectionQuery: sectionQuery.trim() || undefined,
      excludedDocumentIds: excludedDocumentIds.length > 0 ? excludedDocumentIds : undefined,
      status,
      includeTrashed,
      includeUnassigned,
      tiers: selectedTiers,
      types: selectedTypes.length > 0 ? selectedTypes : undefined,
      limit: 100,
      inForceFilter,
    }),
    [
      excludedDocumentIds,
      includeTrashed,
      includeUnassigned,
      query,
      sectionQuery,
      selectedTiers,
      selectedTypes,
      status,
      inForceFilter,
    ]
  )
  const [appliedFilters, setAppliedFilters] = useState<ScopeTierBatchSearchFilters | null>(null)

  const canRunSearch = draftFilters.q.length >= MIN_BATCH_QUERY_LENGTH
  const searchQuery = useScopeTierBatchSearch(appliedFilters ?? { q: '', limit: 100 }, {
    enabled: !!appliedFilters && appliedFilters.q.length >= MIN_BATCH_QUERY_LENGTH,
    queryKeySuffix: searchVersion,
  })

  const items = useMemo(() => {
    const pages = searchQuery.data?.pages ?? []
    const seen = new Set<string>()
    const next: Array<(typeof pages)[number]['items'][number]> = []
    for (const page of pages) {
      for (const item of page.items) {
        if (seen.has(item.documentId)) {
          continue
        }
        seen.add(item.documentId)
        next.push(item)
      }
    }
    return next
  }, [searchQuery.data?.pages])

  const firstPage = searchQuery.data?.pages?.[0]
  const totalMatchedDocuments = firstPage?.totalMatchedDocuments ?? 0
  const totalMatchedParagraphs = firstPage?.totalMatchedParagraphs ?? 0
  const resultsState = getBatchSearchResultsState({
    hasAppliedFilters: !!appliedFilters && appliedFilters.q.length >= MIN_BATCH_QUERY_LENGTH,
    isError: searchQuery.isError,
    isFetching: searchQuery.isFetching,
    hasData: (searchQuery.data?.pages?.length ?? 0) > 0,
    itemCount: items.length,
  })

  useEffect(() => {
    if (items.length === 0) {
      return
    }
    setSelectionById((prev) => mergeDefaultSelection(prev, items.map((item) => item.documentId)))
  }, [items])

  useEffect(() => {
    setSelectionById({})
    setExpandedDocumentId(null)
    setExcludedDocumentIds([])
  }, [searchVersion])

  const selectedDocumentIds = useMemo(
    () => items.filter((item) => selectionById[item.documentId]).map((item) => item.documentId),
    [items, selectionById]
  )

  const matchesQuery = useScopeTierBatchDocumentMatches(
    expandedDocumentId,
    { q: appliedFilters?.q ?? '', sectionQuery: appliedFilters?.sectionQuery, limit: 1000 },
    {
      enabled:
        !!expandedDocumentId &&
        !!appliedFilters &&
        appliedFilters.q.length >= MIN_BATCH_QUERY_LENGTH,
    }
  )

  const bulkMutation = useSetScopeTierStatusesBulk()
  const tagsQuery = useScopeTierTags({ query: batchTagDraft, limit: 100 })
  const isTagAction = action === 'add_tags' || action === 'remove_tags'
  const normalizedBatchTagDraft = normalizeTagInput(batchTagDraft)
  const tagOptions = tagsQuery.data?.items ?? []
  const normalizedExistingTagNames = new Set(tagOptions.map((item) => item.normalized))
  const canCreateTag =
    normalizedBatchTagDraft != null &&
    !normalizedExistingTagNames.has(normalizedBatchTagDraft.toLowerCase())
  const onSearch = () => {
    if (!canRunSearch) {
      setResultMessage(`Search requires at least ${MIN_BATCH_QUERY_LENGTH} characters`)
      return
    }
    setResultMessage(null)
    setAppliedFilters({ ...draftFilters })
    setSearchVersion((prev) => prev + 1)
  }

  const onApply = async () => {
    if (selectedDocumentIds.length === 0) {
      setResultMessage('Select at least one document before applying an action')
      return
    }
    if (selectedDocumentIds.length > 500) {
      setResultMessage('A maximum of 500 selected documents can be applied per request')
      return
    }
    if (isTagAction && batchActionTags.length === 0) {
      setResultMessage('Select at least one tag before applying a tag action')
      return
    }
    const actionLabel =
      action === 'add_tags'
        ? `add tags (${batchActionTags.join(', ')})`
        : action === 'remove_tags'
          ? `remove tags (${batchActionTags.join(', ')})`
          : action
    const confirmed = window.confirm(`Apply "${actionLabel}" to ${selectedDocumentIds.length} selected documents?`)
    if (!confirmed) {
      return
    }
    const result = isTagAction
      ? await bulkMutation.mutateAsync({
          action,
          tags: batchActionTags,
          documentIds: selectedDocumentIds,
        })
      : await bulkMutation.mutateAsync({
          status: action,
          documentIds: selectedDocumentIds,
        })
    setExcludedDocumentIds((prev) => mergeExcludedDocumentIds(prev, selectedDocumentIds))
    setSelectionById((prev) => {
      const next = { ...prev }
      for (const documentId of selectedDocumentIds) {
        next[documentId] = false
      }
      return next
    })
    if (isTagAction) {
      setBatchTagDraft('')
      setBatchActionTags([])
    }
    setResultMessage(
      `Applied to ${result.updatedCount} docs, not found: ${result.notFoundCount}, failed: ${result.failedCount}`
    )
  }

  const onAddBatchActionTag = (rawTag: string) => {
    const normalized = normalizeTagInput(rawTag)
    if (!normalized) {
      return
    }
    setBatchActionTags((prev) => addUniqueTags(prev, [normalized]))
    setBatchTagDraft('')
  }

  const onCreateTagFromMenu = () => {
    if (!normalizedBatchTagDraft) {
      return
    }
    onAddBatchActionTag(normalizedBatchTagDraft)
    setIsTagMenuOpen(false)
  }

  const onRemoveBatchActionTag = (tag: string) => {
    setBatchActionTags((prev) => prev.filter((value) => value.toLowerCase() !== tag.toLowerCase()))
  }

  useEffect(() => {
    if (!isTagMenuOpen) {
      setBatchTagDraft('')
      return
    }
    const timeoutId = window.setTimeout(() => {
      tagMenuInputRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(timeoutId)
  }, [isTagMenuOpen])

  const toggleTier = (tier: 1 | 2 | 3) => {
    setSelectedTiers((prev) =>
      prev.includes(tier) ? prev.filter((value) => value !== tier) : [...prev, tier].sort()
    )
  }

  const toggleFilterType = (typeCode: LegislationTypeCode) => {
    setSelectedTypes((prev) =>
      prev.includes(typeCode)
        ? prev.filter((value) => value !== typeCode)
        : [...prev, typeCode]
    )
  }

  const toggleRow = (documentId: string) => {
    setSelectionById((prev) => ({ ...prev, [documentId]: !prev[documentId] }))
  }

  return (
    <main className='flex flex-1 overflow-hidden'>
      <section className='flex h-full w-full flex-col gap-3 overflow-hidden p-4'>
        <div className='flex items-center justify-between'>
          <h2 className='text-xl font-semibold'>Batch Edit Scope Tier</h2>
          <Button variant='outline' onClick={onDismiss}>
            Exit Batch Mode
          </Button>
        </div>

        <div className='grid grid-cols-1 gap-2 md:grid-cols-2'>
          <Input
            value={query}
            placeholder='Search substring (case-insensitive)'
            onChange={(event) => setQuery(event.target.value)}
          />
          <Input
            value={sectionQuery}
            placeholder='Optional article/section hint (best effort)'
            onChange={(event) => setSectionQuery(event.target.value)}
          />
          <LegislationTypeFilter
            selectedTypes={selectedTypes}
            onToggle={toggleFilterType}
            onClear={() => setSelectedTypes([])}
          />
          <div className='flex items-center gap-2'>
            <select
              className='rounded border px-2 py-1'
              value={status}
              onChange={(event) => setStatus(event.target.value as ScopeTierStatusFilter)}
            >
              <option value='pending'>Pending only</option>
              <option value='approved'>Approved only</option>
              <option value='flagged'>Flagged only</option>
              <option value='trashed'>Trashed only</option>
              <option value='extension_review_needed'>Extension review needed</option>
              <option value='consolidation_review_needed'>Consolidation review needed</option>
              <option value='edited'>Edited (my recent)</option>
              <option value='all'>All statuses</option>
            </select>
            <select
              className='rounded border px-2 py-1'
              value={inForceFilter}
              onChange={(event) =>
                setInForceFilter(event.target.value as ScopeTierInForceFilter)
              }
            >
              <option value='in_force'>In force only</option>
              <option value='not_in_force'>Out of force only</option>
              <option value='all'>Both force states</option>
            </select>
            <label className='flex items-center gap-1 text-sm'>
              <input
                type='checkbox'
                checked={includeUnassigned}
                onChange={(event) => setIncludeUnassigned(event.target.checked)}
              />
              Include unassigned
            </label>
            <label className='flex items-center gap-1 text-sm'>
              <input
                type='checkbox'
                checked={includeTrashed}
                onChange={(event) => setIncludeTrashed(event.target.checked)}
              />
              Include trashed
            </label>
          </div>
          <div className='flex items-center gap-3 text-sm'>
            <span>Include tiers:</span>
            {[1, 2, 3].map((tierValue) => (
              <label key={tierValue} className='flex items-center gap-1'>
                <input
                  type='checkbox'
                  checked={selectedTiers.includes(tierValue as 1 | 2 | 3)}
                  onChange={() => toggleTier(tierValue as 1 | 2 | 3)}
                />
                <span>{tierValue}</span>
              </label>
            ))}
          </div>
        </div>

        <div className='flex items-center gap-2'>
          <Button onClick={onSearch} disabled={!canRunSearch || searchQuery.isFetching}>
            Search
          </Button>
          <span className='text-sm text-muted-foreground'>
            Minimum query length: {MIN_BATCH_QUERY_LENGTH}. Results can shift on live data between pages.
          </span>
        </div>

        {resultsState === 'loading' ? (
          <p className='text-sm text-muted-foreground'>Searching…</p>
        ) : null}

        {resultsState === 'error' ? (
          <div className='rounded border border-destructive/40 bg-destructive/5 p-3 text-sm' role='alert'>
            <p>{`Batch search failed: ${getApiErrorMessage(searchQuery.error)}`}</p>
            <Button
              variant='outline'
              size='sm'
              className='mt-2'
              onClick={() => void searchQuery.refetch()}
            >
              Retry search
            </Button>
          </div>
        ) : null}

        {resultsState === 'empty' || resultsState === 'results' ? (
          <div className='flex items-center gap-3 text-sm'>
            <span>{`Matched documents: ${totalMatchedDocuments}`}</span>
            <span>{`Matched paragraphs: ${totalMatchedParagraphs}`}</span>
            <span>{`Selected: ${selectedDocumentIds.length}`}</span>
            <span>{`Excluded: ${excludedDocumentIds.length}`}</span>
            <button
              type='button'
              className='underline'
              onClick={() => setExcludedDocumentIds([])}
              disabled={excludedDocumentIds.length === 0}
            >
              Reset exclusion
            </button>
            <button
              type='button'
              className='underline'
              onClick={() =>
                setSelectionById(Object.fromEntries(items.map((item) => [item.documentId, true])))
              }
            >
              Select all loaded
            </button>
            <button
              type='button'
              className='underline'
              onClick={() =>
                setSelectionById((prev) =>
                  Object.fromEntries(Object.keys(prev).map((documentId) => [documentId, false]))
                )
              }
            >
              Clear selection
            </button>
          </div>
        ) : null}

        <div className='flex items-center gap-2'>
          <select
            className='rounded border px-2 py-1'
            value={action}
            onChange={(event) => setAction(event.target.value as BatchActionValue)}
          >
            <option value='flagged'>Mark selected as flagged</option>
            <option value='trashed'>Mark selected as trashed</option>
            <option value='pending'>Mark selected as pending</option>
            <option value='approved'>Mark selected as approved</option>
            <option value='add_tags'>Add tags to selected</option>
            <option value='remove_tags'>Remove tags from selected</option>
          </select>
          <Button onClick={() => void onApply()} disabled={bulkMutation.isPending || selectedDocumentIds.length === 0}>
            Apply
          </Button>
        </div>
        {isTagAction ? (
          <div className='space-y-2'>
            <Popover open={isTagMenuOpen} onOpenChange={setIsTagMenuOpen}>
              <PopoverTrigger asChild>
                <button
                  type='button'
                  className='flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-left text-sm shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'
                  onClick={() => setIsTagMenuOpen(true)}
                >
                  <span className='text-muted-foreground'>Search or create tag</span>
                </button>
              </PopoverTrigger>
              <PopoverContent align='start' className='w-80 p-0'>
                <Command shouldFilter={false}>
                  <CommandInput
                    ref={tagMenuInputRef}
                    placeholder='Search or create tag'
                    value={batchTagDraft}
                    onValueChange={setBatchTagDraft}
                  />
                  <CommandList>
                    <CommandEmpty>No matching tags</CommandEmpty>
                    {canCreateTag ? (
                      <CommandItem
                        key={`create-${normalizedBatchTagDraft}`}
                        value={normalizedBatchTagDraft ?? ''}
                        onSelect={onCreateTagFromMenu}
                      >
                        Create "{normalizedBatchTagDraft}"
                      </CommandItem>
                    ) : null}
                    {tagOptions.map((tag) => (
                      <CommandItem
                        key={tag.normalized}
                        value={tag.name}
                        onSelect={() => {
                          onAddBatchActionTag(tag.name)
                          setIsTagMenuOpen(false)
                        }}
                      >
                        <span className='flex-1'>{tag.name}</span>
                        {batchActionTags.some((item) => item.toLowerCase() === tag.name.toLowerCase()) ? (
                          <Check className='size-4 text-muted-foreground' />
                        ) : null}
                      </CommandItem>
                    ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {batchActionTags.length > 0 ? (
              <div className='flex flex-wrap gap-2'>
                {batchActionTags.map((tag) => (
                  <button
                    key={tag}
                    type='button'
                    className='rounded border border-border bg-muted px-2 py-1 text-xs'
                    onClick={() => onRemoveBatchActionTag(tag)}
                  >
                    {tag} x
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {resultMessage ? <div className='text-sm text-foreground'>{resultMessage}</div> : null}

        {resultsState === 'empty' ? (
          <p className='text-sm text-muted-foreground'>No documents matched the current filters.</p>
        ) : null}

        {resultsState === 'results' ? (
        <div className='flex-1 overflow-auto rounded border'>
          {items.map((item) => {
            const selected = selectionById[item.documentId] ?? false
            const isExpanded = expandedDocumentId === item.documentId
            return (
              <div key={item.documentId} className='border-b p-3'>
                <div className='flex items-center justify-between gap-2'>
                  <label className='flex items-center gap-2'>
                    <input
                      type='checkbox'
                      checked={selected}
                      onChange={() => toggleRow(item.documentId)}
                    />
                    <span className='font-medium'>{item.celex}</span>
                    <span className='text-xs text-muted-foreground'>{item.status}</span>
                    <span className='text-xs text-muted-foreground'>{`Matches: ${item.totalMatchesInDoc}`}</span>
                  </label>
                  <button
                    type='button'
                    className='text-sm underline'
                    onClick={() => setExpandedDocumentId(isExpanded ? null : item.documentId)}
                  >
                    {isExpanded ? 'Hide matches' : 'Show all matches'}
                  </button>
                </div>
                <div className='mt-1 whitespace-pre-wrap text-sm'>
                  {renderHighlightedText(
                    item.previewRawText,
                    item.previewMatchStart,
                    appliedFilters?.q.length ?? 0
                  )}
                </div>
                {isExpanded ? (
                  <div className='mt-2 space-y-2 border-l pl-4'>
                    {matchesQuery.isLoading ? <div className='text-sm'>Loading matches...</div> : null}
                    {(matchesQuery.data?.items ?? []).map((match) => (
                      <div key={match.blockId} className='whitespace-pre-wrap text-sm'>
                        {renderHighlightedText(
                          match.rawText,
                          match.matchStart,
                          appliedFilters?.q.length ?? 0
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
        ) : null}

        {resultsState === 'results' && searchQuery.hasNextPage ? (
          <div>
            <Button
              variant='outline'
              disabled={searchQuery.isFetchingNextPage}
              onClick={() => void searchQuery.fetchNextPage()}
            >
              {searchQuery.isFetchingNextPage ? 'Loading...' : 'Load next page'}
            </Button>
          </div>
        ) : null}
      </section>
    </main>
  )
}

function renderHighlightedText(text: string, matchStart: number, queryLength: number) {
  if (queryLength <= 0 || matchStart < 0 || matchStart >= text.length) {
    return text
  }
  const safeEnd = Math.min(matchStart + queryLength, text.length)
  return (
    <>
      {text.slice(0, matchStart)}
      <mark>{text.slice(matchStart, safeEnd)}</mark>
      {text.slice(safeEnd)}
    </>
  )
}

export function mergeDefaultSelection(
  prev: Record<string, boolean>,
  documentIds: string[]
): Record<string, boolean> {
  const next = { ...prev }
  for (const documentId of documentIds) {
    if (!(documentId in next)) {
      next[documentId] = true
    }
  }
  return next
}

export function mergeExcludedDocumentIds(prev: string[], next: string[]): string[] {
  return Array.from(new Set([...prev, ...next]))
}

export function normalizeTagInput(value: string): string | null {
  const normalized = value.trim().replace(/\s+/g, ' ')
  return normalized.length > 0 ? normalized : null
}

export function addUniqueTags(prev: string[], incoming: string[]): string[] {
  const seen = new Set(prev.map((item) => item.toLowerCase()))
  const next = [...prev]
  for (const raw of incoming) {
    const normalized = normalizeTagInput(raw)
    if (!normalized) {
      continue
    }
    const key = normalized.toLowerCase()
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    next.push(normalized)
  }
  return next
}
